/**
 * Accès au domaine maintenance : fiche de vie, check-list, interventions.
 *
 * La fiche de vie est un historique de consultation (`docs/DECISIONS.md` D89) : la création d'un
 * événement (opération de maintenance) se fait depuis l'onglet « Action » de la fiche GRV, pas
 * depuis cet écran.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, requete } from "../../shared/api/client";
import type {
  ChecklistEnregistree,
  EnregistrementChecklist,
  EvenementFicheDeVie,
  Intervention,
  LigneMaintenanceTransversale,
  QuestionChecklist,
  TypeEvenement,
} from "../../shared/api/types";

export function useVueTransversale(filtres: { client?: string }) {
  return useQuery({
    queryKey: ["maintenance", "transversal", filtres],
    queryFn: () =>
      api.lire<LigneMaintenanceTransversale[]>(
        "/v1/maintenance" +
          requete({
            "filter[client]": filtres.client,
            // Une ligne par GRV, sans pagination à l'écran (`EcranMaintenance.tsx`, tri/recherche
            // côté client) : la taille de page doit couvrir la flotte cible entière (500 GRV,
            // `docs/agent-spec/00-README.md`), pas seulement une page de 50.
            pageSize: 500,
          }),
      ),
  });
}

export function useTypesEvenements() {
  return useQuery({
    // Référentiel commun à tous les GRV : une seule entrée de cache, pas une par fiche.
    queryKey: ["maintenance", "types-evenements"],
    queryFn: () => api.lire<TypeEvenement[]>("/v1/maintenance/types-evenements"),
    // Rarement modifié (écran de paramétrage) : pas besoin de le relire à chaque changement d'onglet.
    staleTime: 5 * 60_000,
  });
}

export function useFicheDeVie(numero: string) {
  return useQuery({
    queryKey: ["maintenance", "fiche-de-vie", numero],
    queryFn: () =>
      api.lire<EvenementFicheDeVie[]>(`/v1/grv/${encodeURIComponent(numero)}/fiche-de-vie?pageSize=50`),
  });
}

export function useAjouterIntervention(numero: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: Intervention) =>
      api.creer<EvenementFicheDeVie>(`/v1/grv/${encodeURIComponent(numero)}/interventions`, corps),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["maintenance", "fiche-de-vie", numero] });
      // Une intervention peut changer le statut opérationnel du GRV (`20-formulaires.md`) : la
      // fiche doit être relue.
      void client.invalidateQueries({ queryKey: ["grv", "fiche", numero] });
    },
  });
}

export function useQuestionsChecklist() {
  return useQuery({
    // Référentiel commun à tous les GRV : une seule entrée de cache, pas une par fiche.
    queryKey: ["maintenance", "checklist-questions"],
    queryFn: () => api.lire<QuestionChecklist[]>("/v1/maintenance/checklist-questions"),
    staleTime: 5 * 60_000,
  });
}

export function useHistoriqueChecklists(numero: string) {
  return useQuery({
    queryKey: ["maintenance", "checklists", numero],
    queryFn: () =>
      api.lire<ChecklistEnregistree[]>(`/v1/grv/${encodeURIComponent(numero)}/checklists`),
  });
}

function invalider(client: ReturnType<typeof useQueryClient>, numero: string) {
  void client.invalidateQueries({ queryKey: ["maintenance", "checklists", numero] });
}

export function useDemarrerChecklist(numero: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.creer<ChecklistEnregistree>(`/v1/grv/${encodeURIComponent(numero)}/checklists`),
    onSuccess: () => invalider(client, numero),
  });
}

export function useEnregistrerBrouillonChecklist(numero: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ checklistId, corps }: { checklistId: string; corps: EnregistrementChecklist }) =>
      api.remplacer<ChecklistEnregistree>(
        `/v1/grv/${encodeURIComponent(numero)}/checklists/${encodeURIComponent(checklistId)}`,
        corps,
      ),
    onSuccess: () => invalider(client, numero),
  });
}

export function useTerminerChecklist(numero: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ checklistId, corps }: { checklistId: string; corps: EnregistrementChecklist }) =>
      api.creer<ChecklistEnregistree>(
        `/v1/grv/${encodeURIComponent(numero)}/checklists/${encodeURIComponent(checklistId)}/terminer`,
        corps,
      ),
    onSuccess: () => invalider(client, numero),
  });
}

export function useValiderChecklist(numero: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (checklistId: string) =>
      api.creer<ChecklistEnregistree>(
        `/v1/grv/${encodeURIComponent(numero)}/checklists/${encodeURIComponent(checklistId)}/valider`,
      ),
    onSuccess: () => invalider(client, numero),
  });
}
